import { useMemo, useState, type Key } from "react";
import { Button, Empty, Flex, Input, Select, Skeleton, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { formatClassification, type AdminNode } from "../adminTypes";
import { buildTreeIndex } from "../treeIndex";
import { StatusTag } from "./StatusTag";

export type AdminTablePanelProps = {
  readonly nodes: readonly AdminNode[];
  readonly loading: boolean;
  readonly selectedId: string | null;
  readonly query: string;
  readonly statusFilter: "ACTIVE" | "INACTIVE" | "MERGED" | undefined;
  readonly classificationLabel: string;
  readonly entityNoun: string;
  readonly onSelect: (id: string) => void;
  readonly onChangeQuery: (q: string) => void;
  readonly onChangeStatus: (s: "ACTIVE" | "INACTIVE" | "MERGED" | undefined) => void;
  readonly onCreateRoot: () => void;
};

type AdminTableRow = AdminNode & {
  children?: AdminTableRow[];
};

function matchesQuery(node: AdminNode, q: string): boolean {
  const trimmed = q.trim().toLowerCase();
  if (!trimmed) return true;
  return node.name.toLowerCase().includes(trimmed) || node.code.toLowerCase().includes(trimmed);
}

function buildTreeRows(
  parentNodes: readonly AdminNode[],
  childrenByParent: ReadonlyMap<string, AdminNode[]>,
): AdminTableRow[] {
  return parentNodes.map((node) => {
    const children = childrenByParent.get(node.id);
    if (children && children.length > 0) {
      return {
        ...node,
        children: buildTreeRows(children, childrenByParent),
      };
    }
    const { ...leaf } = node;
    return leaf;
  });
}

export function AdminTablePanel({
  nodes,
  loading,
  selectedId,
  query,
  statusFilter,
  classificationLabel,
  entityNoun,
  onSelect,
  onChangeQuery,
  onChangeStatus,
  onCreateRoot,
}: AdminTablePanelProps) {
  const [userExpandedKeys, setUserExpandedKeys] = useState<readonly Key[] | null>(null);

  const byId = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);

  const parentKeys = useMemo(() => {
    const seen = new Set<Key>();
    const result: Key[] = [];
    for (const node of nodes) {
      if (node.parentId != null && !seen.has(node.parentId)) {
        seen.add(node.parentId);
        result.push(node.parentId);
      }
    }
    return result;
  }, [nodes]);

  const hasFilter = Boolean(query.trim() || statusFilter);

  const tableData: AdminTableRow[] = useMemo(() => {
    if (hasFilter) {
      return nodes
        .filter(
          (node) =>
            matchesQuery(node, query) && (!statusFilter || node.status === statusFilter),
        )
        .map((node) => {
          const { ...leaf } = node;
          return leaf;
        });
    }

    const index = buildTreeIndex(nodes);
    return buildTreeRows(index.roots, index.childrenByParent);
  }, [nodes, query, statusFilter, hasFilter]);

  const expandedRowKeys = useMemo(() => {
    if (hasFilter) {
      return undefined;
    }
    return userExpandedKeys ?? parentKeys;
  }, [hasFilter, userExpandedKeys, parentKeys]);

  const columns: ColumnsType<AdminTableRow> = useMemo(
    () => [
      {
        title: "Tên đơn vị",
        dataIndex: "name",
        key: "name",
        ellipsis: true,
        render: (_: unknown, record: AdminTableRow) => (
          <Button
            type="link"
            onClick={(event) => {
              event.stopPropagation();
              onSelect(record.id);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.stopPropagation();
                onSelect(record.id);
              }
            }}
            style={{
              padding: 0,
              height: "auto",
              textAlign: "left",
              maxWidth: "calc(100% - 24px)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "inline-block",
              verticalAlign: "middle",
            }}
          >
            {record.name}
          </Button>
        ),
      },
      {
        title: "Mã",
        dataIndex: "code",
        key: "code",
        width: 170,
        ellipsis: true,
        render: (code: string) => (
          <Typography.Text type="secondary" style={{ whiteSpace: "nowrap" }}>
            {code}
          </Typography.Text>
        ),
      },
      {
        title: classificationLabel,
        dataIndex: "classification",
        key: "classification",
        width: 150,
        ellipsis: true,
        render: (classification: string) => formatClassification(classification),
      },
      {
        title: "Thuộc quản lý",
        key: "parent",
        width: 250,
        ellipsis: true,
        render: (_: unknown, record: AdminTableRow) => {
          if (record.parentId == null) {
            return "Cấp gốc";
          }
          const parent = byId.get(record.parentId);
          return (
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {parent ? parent.name : "(ngoài cây hiển thị)"}
            </span>
          );
        },
      },
      {
        title: "Trạng thái",
        dataIndex: "status",
        key: "status",
        width: 130,
        render: (status: string) => <StatusTag status={status} />,
      },
    ],
    [classificationLabel, byId, onSelect],
  );

  if (loading) {
    return (
      <div className="admin-table-panel" role="status" aria-label={`Đang tải cây ${entityNoun}`}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  if (nodes.length === 0) {
    return (
      <div className="admin-table-panel">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={`Chưa có ${entityNoun} nào`}
        >
          <Button type="primary" onClick={onCreateRoot}>
            Tạo {entityNoun} cấp gốc
          </Button>
        </Empty>
      </div>
    );
  }

  return (
    <Flex vertical gap={12} className="admin-table-panel">
      <Flex gap={12} wrap>
        <Input.Search
          allowClear
          placeholder="Tìm theo tên hoặc mã"
          aria-label="Tìm kiếm theo tên hoặc mã"
          value={query}
          onChange={(event) => onChangeQuery(event.target.value)}
          style={{ maxWidth: 320 }}
        />
        <Select
          allowClear
          placeholder="Tất cả trạng thái"
          value={statusFilter}
          onChange={(value) => onChangeStatus(value)}
          style={{ width: 160 }}
          options={[
            { value: "ACTIVE", label: "Hoạt động" },
            { value: "INACTIVE", label: "Đã đóng" },
            { value: "MERGED", label: "Đã sáp nhập" },
          ]}
        />
      </Flex>
      <Table<AdminTableRow>
        aria-label={`Danh sách ${entityNoun}`}
        rowKey="id"
        tableLayout="fixed"
        columns={columns}
        dataSource={tableData}
        pagination={false}
        {...(expandedRowKeys
          ? {
              expandedRowKeys: [...expandedRowKeys],
              onExpandedRowsChange: (keys: readonly Key[]) => setUserExpandedKeys(keys as Key[]),
            }
          : {})}
        rowClassName={(record) => (record.id === selectedId ? "admin-table-row-selected" : "")}
        onRow={(record) => ({
          onClick: () => onSelect(record.id),
          "aria-current": record.id === selectedId ? ("true" as const) : undefined,
        })}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={`Không tìm thấy ${entityNoun} phù hợp bộ lọc`}
            >
              <Button
                onClick={() => {
                  onChangeQuery("");
                  onChangeStatus(undefined);
                }}
              >
                Xóa bộ lọc
              </Button>
            </Empty>
          ),
        }}
      />
    </Flex>
  );
}
